import { getToken } from "@ptx-autonomous/libs/api";
import { useQuery } from "@tanstack/vue-query";
import { type FeatureCollection, type Feature } from "geojson";
import JSZip from "jszip";
import { Api } from "./models";
import { ref, onUnmounted } from "vue";

const defaultUrl = import.meta.env.VITE_AGCO_DATA_EXCHANGE_API_BASE_URL as
	| string
	| undefined;

const dataExchangeServiceHttpClient = new Api({
	baseApiParams: { secure: true },
	securityWorker: async () => {
		const headers: Record<string, string> = {
			Authorization: `${await getToken("access")}`,
		};

		return { headers };
	},
});

export const initDataExchangeServiceClient = () => {
	dataExchangeServiceHttpClient.baseUrl = `${defaultUrl}`;
};

export const useDataExchangeServiceHttp = (): Api<unknown> =>
	dataExchangeServiceHttpClient as Api<unknown>;

export async function unzipSpatialFileStream(
	file: Blob,
): Promise<FeatureCollection | undefined> {
	try {
		const zip = new JSZip();
		const unzipped = await zip.loadAsync(file);
		const files = Object.values(unzipped.files);
		const fileData = await files[0].async("string");
		return JSON.parse(fileData) as FeatureCollection;
	} catch (err) {
		console.error("Error encountered while unzipping file.");
		console.error(err);
	}
}

export async function parseSpatialFileStream(
	file: Blob,
): Promise<FeatureCollection | undefined> {
	try {
		const fileContent = await file.text();
		const parsed = JSON.parse(fileContent) as Feature | FeatureCollection;

		if (parsed.type === "Feature") {
			return {
				type: "FeatureCollection",
				features: [parsed],
			};
		} else if (parsed.type === "FeatureCollection") {
			return parsed;
		} else {
			throw new Error("Unsupported GeoJSON type");
		}
	} catch (err) {
		console.error("Error encountered while parsing file.");
		console.error(err);
		return undefined;
	}
}

async function createGeoJsonConversionJob(http: Api<unknown>) {
	const data = {
		action: "convert",
		target_format: "GeoJson",
		auto_source_exclude_formats: ["JD*"],
	};

	const res = await http.jobs.jobsCreate(data);

	return {
		jobId: res.data.job_id!,
		uploadUrl: res.data.upload_url!,
		downloadUrl: res.data.download_url!,
		expires: res.data.expires!,
	};
}

async function uploadFileToJob(uploadUrl: string, file: File) {
	await fetch(uploadUrl, {
		method: "PUT",
		headers: {
			"Content-Type": "application/zip",
		},
		body: file,
	});
}

type SupportedImportFormat = {
	formats: string[];
	sources: {
		organisation: string;
		source: string;
		type: string;
		version: string;
	}[];
	name: string;
	description: string;
	id: string;
};

export function useSupportedImportFormats(http: Api<unknown>) {
	async function getSupportedImportFormats(): Promise<string[]> {
		const res = await http.convert.inputFormatsList();
		const data = res.data as SupportedImportFormat[];
		const supportedImportFormats = data.flatMap(item => item.formats);
		return supportedImportFormats;
	}

	return useQuery({
		queryKey: ["supportedImportFormats"],
		queryFn: getSupportedImportFormats,
		placeholderData: [],
	});
}

interface JobStatusMessage {
	job?: string;
	status?: "created" | "processing" | "failed" | "error" | "completed";
	messages?: string;
}

const DATA_SERVICE_BASE_URL = "v3.dxfamt.dev.agco-opsmgmt.com";

function useJobStatusWebSocket() {
	const ws = ref<WebSocket | null>(null);
	const isConnected = ref(false);
	const messageHandlers = new Map<
		string,
		(message: JobStatusMessage) => void
	>();

	async function connect() {
		if (ws.value) {
			ws.value.close();
		}

		const token = await getToken("access");
		const wsBaseUrl = `wss://ws.${DATA_SERVICE_BASE_URL}`;
		const wsUrl = `${wsBaseUrl}?token=${token}`;

		return new Promise<void>((resolve, reject) => {
			ws.value = new WebSocket(wsUrl);

			ws.value.onopen = () => {
				isConnected.value = true;
				resolve();
			};

			ws.value.onerror = error => {
				console.error("WebSocket error:", error);
				reject(error);
			};

			ws.value.onclose = () => {
				isConnected.value = false;
				messageHandlers.clear();
			};

			ws.value.onmessage = event => {
				const message = JSON.parse(event.data) as JobStatusMessage;

				if (message.job) {
					const handler = messageHandlers.get(message.job);
					if (handler) {
						handler(message);
					}
				}
			};
		});
	}

	function listenForJob(
		jobId: string,
		onStatusUpdate: (message: JobStatusMessage) => void,
	) {
		if (!ws.value || !isConnected.value) {
			console.error(
				"WebSocket not connected. Cannot listen for job:",
				jobId,
			);
			return;
		}

		messageHandlers.set(jobId, onStatusUpdate);
	}

	function stopListeningForJob(jobId: string) {
		messageHandlers.delete(jobId);
	}

	function disconnect() {
		if (ws.value) {
			ws.value.close();
			ws.value = null;
		}
	}

	onUnmounted(() => {
		disconnect();
	});

	return {
		connect,
		listenForJob,
		stopListeningForJob,
		disconnect,
		isConnected,
	};
}

/**
 * Main entrypoint composable for converting into GeoJSON using the Randers data exchange API.
 * The output geojson can be used to create fields in the application.
 */
export function useGeoJsonConversion(http: Api<unknown>) {
	const geojson = ref<FeatureCollection>();
	const isConverting = ref(false);
	const error = ref<string>();

	const { connect, listenForJob, isConnected } = useJobStatusWebSocket();

	async function convertFile(file: File) {
		try {
			error.value = undefined;
			isConverting.value = true;
			geojson.value = undefined;

			if (!isConnected.value) {
				await connect();
			}

			const jobData = await createGeoJsonConversionJob(http);

			listenForJob(jobData.jobId, async message => {
				if (message.status === "completed") {
					try {
						const response = await fetch(jobData.downloadUrl);
						if (response.ok) {
							const blob = await response.blob();
							const convertedGeojson =
								await unzipSpatialFileStream(blob);

							if (convertedGeojson) {
								geojson.value = {
									type: "FeatureCollection",
									features: convertedGeojson.features.filter(
										feature =>
											feature.geometry.type ===
												"Polygon" ||
											feature.geometry.type ===
												"MultiPolygon",
									),
								};
							}
						}
					} catch (err) {
						error.value =
							"Error downloading converted file: " +
							(err as Error).message;
					} finally {
						isConverting.value = false;
					}
				} else if (
					message.status === "failed" ||
					message.status === "error"
				) {
					error.value = `Job failed: ${message.messages || "Unknown error"}`;
					isConverting.value = false;
					geojson.value = undefined;
				}
			});

			await uploadFileToJob(jobData.uploadUrl, file);
		} catch (err) {
			error.value = (err as Error).message;
			isConverting.value = false;
			geojson.value = undefined;
		}
	}

	return {
		geojson,
		isConverting,
		error,
		convertFile,
		connect,
	};
}
